import React, { useState, useEffect } from "react";
import { Button, Rating, TextField, Box, Typography } from "@mui/material";
import { AlertTriangle, Successfully } from "../../Common/CommonImages";
import { handleDynamicRequest, BASE_URL } from "../../Common/CommonExport";
import { useSelector, useDispatch } from "react-redux";
import { setLogout } from "../../store/Test/action";
import { setCandidateLogout } from "../../store/Candidate/action";
import { exitFullScreen } from "../../utils/fullscreen";
import { handleBeforeUnload as globalHandleBeforeUnload } from "../../Common/EventHandlers";

import { Star } from "@mui/icons-material";

function FeedbackPage() {
  const [experienceRating, setExperienceRating] = useState(0);
  const [questionRating, setQuestionRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const dispatch = useDispatch();

  const testId = useSelector((state) => state.testReducer.testId);
  const candidateId = useSelector((state) => state.candidateReducer.id);
  const company = useSelector((state) => state.testReducer.company);

  useEffect(() => {
    // Fullscreen will now stay active until feedback is submitted
    // as requested by the user.

    const handleBackButton = (e) => {
      e.preventDefault();
      window.history.pushState(null, null, window.location.pathname);
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", handleBackButton);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Store references for cleanup
    window.feedbackHandlers = {
      handleBackButton,
      handleBeforeUnload,
    };

    return () => {
      window.removeEventListener("popstate", handleBackButton);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.onbeforeunload = null;
    };
  }, []);

  const handleFeedbackSubmit = async () => {
    setIsSubmitting(true);

    try {
      const url = `${BASE_URL}/public/assessment/save-candidate-feedback`;
      const requestBody = {
        assessmentId: testId,
        candidateAssessmentId: candidateId,
        experienceRating: experienceRating,
        questionRating: questionRating,
      };

      if (feedback && feedback.trim().length > 0) {
        requestBody.feedback = feedback.trim();
      }
      const method = "POST";

      const wrongResponse = () => {
        console.log("Failed to save feedback");
        setIsSubmitting(false);
        showSuccessAndRedirect();
      };

      const rightResponse = () => {
        console.log("Feedback saved successfully");
        setIsSubmitting(false);
        showSuccessAndRedirect();
      };

      handleDynamicRequest(
        method,
        url,
        requestBody,
        wrongResponse,
        rightResponse,
      );
    } catch (error) {
      console.error("Error saving feedback:", error);
      setIsSubmitting(false);
      showSuccessAndRedirect();
    }
  };

  const showSuccessAndRedirect = () => {
    setShowSuccessModal(true);

    setTimeout(() => {
      // Clear ALL event listeners to prevent any browser alerts
      if (window.feedbackHandlers) {
        window.removeEventListener(
          "beforeunload",
          window.feedbackHandlers.handleBeforeUnload,
        );
        window.removeEventListener(
          "popstate",
          window.feedbackHandlers.handleBackButton,
        );
      }
      
      // Also remove the global beforeunload listener from the main test
      window.removeEventListener("beforeunload", globalHandleBeforeUnload);

      // Explicitly nullify the handlers
      window.onbeforeunload = null;
      window.onunload = null;

      dispatch(setLogout());
      dispatch(setCandidateLogout());

      // Exit fullscreen ONLY now at the very end
      exitFullScreen().catch(() => {});

      localStorage.clear();

      // Navigate to home page (Who Are We page) without any dialogs
      window.location.replace("/");
    }, 2000);
  };

  return (
    <>
      <Box
        className="App"
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f5f5f5",
          p: 2,
          overflowY: "auto",
        }}
      >
        {!showSuccessModal ? (
          <Box
            className="feedback-page-container"
            sx={{
              maxWidth: "700px",
              width: { base: "100%", sm: "100%" },
              bgcolor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              p: { base: 3, sm: 5 },
              m: "auto", // Center both vertically and horizontally safely
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { base: "column", sm: "row" },
                alignItems: "center",
                mb: 3,
                textAlign: { base: "center", sm: "left" },
              }}
            >
              <Box
                sx={{
                  width: "48px",
                  height: "48px",
                  bgcolor: "#fff0e7",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  mr: { base: 0, sm: 2 },
                  mb: { base: 2, sm: 0 },
                }}
              >
                <Star sx={{ fontSize: "30px", color: "#ff6812" }} />
              </Box>
              <div>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    fontSize: { base: "20px", sm: "24px" },
                    fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  Assessment Feedback
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    color: "#666",
                    fontSize: "14px",
                    fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  Please provide your feedback to complete your assessment
                </Typography>
              </div>
            </Box>

            <Box
              sx={{
                mb: 4,
                p: 2,
                bgcolor: "#e7f5ff",
                borderRadius: "4px",
                borderLeft: "4px solid #1976d2",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#1976d2",
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Your feedback is required to complete the assessment process.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: "#363E45",
                  fontSize: "16px",
                  mb: 1.5,
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Rate the Assessment Experience
                <span style={{ color: "#ff0000", marginLeft: "4px" }}>*</span>
              </Typography>
              <Rating
                value={experienceRating}
                onChange={(e, newValue) => setExperienceRating(newValue)}
                size="large"
                disabled={isSubmitting}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: "#363E45",
                  fontSize: "16px",
                  mb: 1.5,
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Rate the Assessment Questions
                <span style={{ color: "#ff0000", marginLeft: "4px" }}>*</span>
              </Typography>
              <Rating
                value={questionRating}
                onChange={(e, newValue) => setQuestionRating(newValue)}
                size="large"
                disabled={isSubmitting}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: "#363E45",
                  fontSize: "16px",
                  mb: 1.5,
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Additional Feedback (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Share your thoughts about the assessment..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={isSubmitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "'Roboto', sans-serif",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                bgcolor: "#fff3cd",
                padding: "12px",
                borderRadius: "4px",
                mb: 3,
                alignItems: "flex-start",
              }}
            >
              <img
                src={AlertTriangle}
                alt=""
                style={{ width: "20px", marginTop: "2px" }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#856404",
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                Your assessment will be completed only after you provide
                feedback.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                onClick={handleFeedbackSubmit}
                disabled={isSubmitting || !experienceRating || !questionRating}
                sx={{
                  textTransform: "capitalize",
                  fontFamily: "'Roboto', sans-serif",
                  color: "#ffffff",
                  px: 4,
                  py: 1.5,
                  width: { base: "100%", sm: "auto" }, // Full width on mobile
                  bgcolor:
                    !experienceRating || !questionRating
                      ? "#cccccc"
                      : "#ff6812",
                  "&:hover": {
                    bgcolor:
                      !experienceRating || !questionRating
                        ? "#cccccc"
                        : "#e55a0f",
                  },
                  cursor:
                    !experienceRating || !questionRating
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isSubmitting
                  ? "Completing Assessment..."
                  : "Complete Assessment"}
              </Button>
            </Box>
          </Box>
        ) : (
          <div
            className="modal fade alert-model show d-block"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-body">
                  <div className="submittedcontent">
                    <img src={Successfully} alt="" className="successfully" />
                    <h3>Test Submitted!</h3>
                    <p>
                      Your test is successfully submitted. Further details and
                      procedures will be sent to you via email by {company}.
                      Till then be prepared and all the best for future.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Box>
    </>
  );
}

export default FeedbackPage;
